import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Dropdown, Field, Input, Option, Spinner, Text, makeStyles, tokens,
} from '@fluentui/react-components';
import { NumberInput } from '../shared/NumberInput';
import { useT } from '../../i18n';
import { useFieldErrors } from '../../hooks/useFieldErrors';
import { createTransfer, type Product, type TransferInput } from '../../api/inventory.api';

export interface TransferWarehouse {
  warehouseId: string;
  name: string;
}

interface TransferDialogProps {
  open: boolean;
  onClose: () => void;
  warehouses: TransferWarehouse[];
  products: Product[];
  /** Bodega preseleccionada como origen (la que está visible en Inventario). */
  defaultFromWarehouseId?: string;
}

interface TransferForm {
  fromWarehouseId: string;
  toWarehouseId:   string;
  productId:       string;
  qty:             string;
  note:            string;
}

const EMPTY_FORM: TransferForm = { fromWarehouseId: '', toWarehouseId: '', productId: '', qty: '', note: '' };

const useStyles = makeStyles({
  grid: { display: 'flex', flexDirection: 'column', gap: '12px' },
  row:  { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' },
  error: { color: tokens.colorStatusDangerForeground1, fontSize: '12px' },
});

/**
 * Traspaso entre dos bodegas de la empresa. El backend valida permiso
 * (inventory:write), pertenencia de las bodegas y stock suficiente; aquí solo se
 * valida lo obvio para dar retroalimentación inmediata.
 */
export function TransferDialog({ open, onClose, warehouses, products, defaultFromWarehouseId }: TransferDialogProps) {
  const s  = useStyles();
  const t  = useT();
  const qc = useQueryClient();

  const [form, setForm] = useState<TransferForm>({ ...EMPTY_FORM, fromWarehouseId: defaultFromWarehouseId ?? '' });
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const set = (patch: Partial<TransferForm>) => {
    setForm(f => ({ ...f, ...patch }));
    Object.keys(patch).forEach(k => fe.clear(k));
  };

  const reset = () => {
    setForm({ ...EMPTY_FORM, fromWarehouseId: defaultFromWarehouseId ?? '' });
    fe.clear();
    setError('');
  };

  const close = () => { reset(); onClose(); };

  const fromName = warehouses.find(w => w.warehouseId === form.fromWarehouseId)?.name;
  const toName   = warehouses.find(w => w.warehouseId === form.toWarehouseId)?.name;
  const selectedProduct = products.find(p => p.productId === form.productId);

  const mut = useMutation({
    mutationFn: () => {
      const input: TransferInput = {
        fromWarehouseId: form.fromWarehouseId,
        toWarehouseId:   form.toWarehouseId,
        productId:       form.productId,
        qty:             parseFloat(form.qty) || 0,
        note:            form.note.trim() || undefined,
      };
      return createTransfer(input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock'] });
      qc.invalidateQueries({ queryKey: ['movements'] });
      close();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.inventory.transferError),
  });

  const handleSubmit = () => {
    setError('');
    const sameWarehouse = !!form.fromWarehouseId && form.fromWarehouseId === form.toWarehouseId;
    const ok = fe.validate({
      fromWarehouseId: !form.fromWarehouseId && t.inventory.transferWarehouseRequired,
      toWarehouseId: (!form.toWarehouseId && t.inventory.transferWarehouseRequired)
        || (sameWarehouse && t.inventory.transferSameWarehouse),
      productId: !form.productId && t.inventory.productRequired,
      qty: (!form.qty || parseFloat(form.qty) <= 0) && t.inventory.qtyRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  const enoughWarehouses = warehouses.length >= 2;

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) close(); }} modalType="modal">
      <DialogSurface style={{ maxWidth: '520px' }}>
        <DialogBody>
          <DialogTitle>{t.inventory.transferDialogTitle}</DialogTitle>
          <DialogContent>
            {!enoughWarehouses ? (
              <Text>{t.inventory.transferNeedTwoWarehouses}</Text>
            ) : (
              <div className={s.grid}>
                <div className={s.row}>
                  <Field label={t.inventory.fieldFromWarehouse} {...fe.props('fromWarehouseId')}>
                    <Dropdown
                      selectedOptions={form.fromWarehouseId ? [form.fromWarehouseId] : []}
                      value={fromName ?? t.inventory.selectWarehouse}
                      onOptionSelect={(_, d) => set({ fromWarehouseId: (d.optionValue as string) ?? '' })}
                      disabled={mut.isPending}
                    >
                      {warehouses.map(w => (
                        <Option key={w.warehouseId} value={w.warehouseId}>{w.name}</Option>
                      ))}
                    </Dropdown>
                  </Field>
                  <Field label={t.inventory.fieldToWarehouse} {...fe.props('toWarehouseId')}>
                    <Dropdown
                      selectedOptions={form.toWarehouseId ? [form.toWarehouseId] : []}
                      value={toName ?? t.inventory.selectWarehouse}
                      onOptionSelect={(_, d) => set({ toWarehouseId: (d.optionValue as string) ?? '' })}
                      disabled={mut.isPending}
                    >
                      {warehouses.map(w => (
                        <Option key={w.warehouseId} value={w.warehouseId}>{w.name}</Option>
                      ))}
                    </Dropdown>
                  </Field>
                </div>

                <Field label={t.inventory.fieldProduct} {...fe.props('productId')}>
                  <Dropdown
                    selectedOptions={form.productId ? [form.productId] : []}
                    value={selectedProduct?.name ?? t.inventory.selectProduct}
                    onOptionSelect={(_, d) => set({ productId: (d.optionValue as string) ?? '' })}
                    disabled={mut.isPending}
                  >
                    {products.map(p => (
                      <Option key={p.productId} value={p.productId}>{p.name}</Option>
                    ))}
                  </Dropdown>
                </Field>

                <Field label={`${t.inventory.fieldQty}${selectedProduct ? ` (${selectedProduct.unit})` : ''}`} {...fe.props('qty')}>
                  <NumberInput
                    value={form.qty}
                    onValueChange={(v) => set({ qty: v })}
                    placeholder={t.inventory.fieldQtyPlaceholder}
                    disabled={mut.isPending}
                  />
                </Field>

                <Field label={t.inventory.fieldTransferNote}>
                  <Input
                    value={form.note}
                    onChange={(_, d) => set({ note: d.value })}
                    placeholder={t.inventory.fieldTransferNotePlaceholder}
                    disabled={mut.isPending}
                  />
                </Field>

                {error && <Text className={s.error}>{error}</Text>}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={close} disabled={mut.isPending}>{t.inventory.cancel}</Button>
            {enoughWarehouses && (
              <Button
                appearance="primary"
                onClick={handleSubmit}
                disabled={mut.isPending}
                icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
              >
                {t.inventory.transferSubmit}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
